/**
 * Contact Types for FBO PlanMAX
 * Contacts are associated with Clients
 */

// Contact Status enum
export type ContactStatus = 'active' | 'inactive'

export const CONTACT_STATUSES: ContactStatus[] = ['active', 'inactive']

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
}

// Preferred Contact Method enum
export type PreferredContactMethod = 'email' | 'phone' | 'sms'

export const CONTACT_METHODS: PreferredContactMethod[] = ['email', 'phone', 'sms']

export const CONTACT_METHOD_LABELS: Record<PreferredContactMethod, string> = {
  email: 'Email',
  phone: 'Phone',
  sms: 'SMS',
}

/**
 * Full Contact row from database
 */
export interface ContactRow {
  id: string
  client_id: string

  // Personal Information
  first_name: string
  last_name: string
  job_title: string | null

  // Contact Information
  email_primary: string
  email_secondary: string | null
  phone_office: string | null
  phone_mobile: string | null
  phone_other: string | null

  // Role flags
  is_primary_contact: boolean
  is_billing_contact: boolean
  is_operations_contact: boolean

  // Communication preferences
  preferred_contact_method: PreferredContactMethod

  // Status
  status: ContactStatus

  // Notes
  notes: string | null

  // Audit columns
  created_at: string
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
}

/**
 * Data required to create a new contact
 */
export interface ContactInsert {
  client_id: string
  organization_id: string
  first_name: string
  last_name: string
  job_title?: string
  email_primary: string
  email_secondary?: string
  phone_office?: string
  phone_mobile?: string
  phone_other?: string
  is_primary_contact?: boolean
  is_billing_contact?: boolean
  is_operations_contact?: boolean
  preferred_contact_method?: PreferredContactMethod
  status?: ContactStatus
  notes?: string
}

/**
 * Data for updating a contact
 */
export interface ContactUpdate {
  first_name?: string
  last_name?: string
  job_title?: string | null
  email_primary?: string
  email_secondary?: string | null
  phone_office?: string | null
  phone_mobile?: string | null
  phone_other?: string | null
  is_primary_contact?: boolean
  is_billing_contact?: boolean
  is_operations_contact?: boolean
  preferred_contact_method?: PreferredContactMethod
  status?: ContactStatus
  notes?: string | null
}

/**
 * Form data for contact creation/editing
 */
export interface ContactFormData {
  first_name: string
  last_name: string
  job_title?: string
  email_primary: string
  email_secondary?: string
  phone_office?: string
  phone_mobile?: string
  phone_other?: string
  is_primary_contact?: boolean
  is_billing_contact?: boolean
  is_operations_contact?: boolean
  preferred_contact_method?: PreferredContactMethod
  status?: ContactStatus
  notes?: string
}

/**
 * Contact data for table display (subset of full row)
 */
export interface ContactTableRow {
  id: string
  client_id: string
  first_name: string
  last_name: string
  job_title: string | null
  email_primary: string
  phone_mobile: string | null
  phone_office: string | null
  is_primary_contact: boolean
  is_billing_contact: boolean
  is_operations_contact: boolean
  status: ContactStatus
  created_at: string
  deleted_at: string | null
}

/**
 * Get contact display name
 */
export function getContactDisplayName(contact: {
  first_name: string
  last_name: string
}): string {
  return `${contact.first_name} ${contact.last_name}`.trim()
}

/**
 * Get role badges for a contact
 */
export function getContactRoles(contact: {
  is_primary_contact: boolean
  is_billing_contact: boolean
  is_operations_contact: boolean
}): string[] {
  const roles: string[] = []
  if (contact.is_primary_contact) roles.push('Primary')
  if (contact.is_billing_contact) roles.push('Billing')
  if (contact.is_operations_contact) roles.push('Operations')
  return roles
}
